import * as _ from 'lodash-es';
import type { Emoji } from './emojiData';
import emojiList, { skinToneModifiers } from './emojiData';
import type { FuzzySearchMatch } from './search/fuzzySearch';
import fuzzySearch, {
  FuzzySearchIndex,
  FuzzySearchThreshold,
} from './search/fuzzySearch';

export { emojiList, skinToneModifiers };
export type { Emoji };

const index = new FuzzySearchIndex(emojiList, ['name', 'altNames', 'keywords']);

const isIndexMatchingWordBoundary = (
  match: FuzzySearchMatch,
  startIndex: number,
) => startIndex === 0 || !match.value[startIndex - 1]?.match(/^[a-zA-Z0-9]/);

export const searchEmoji = (rawQuery: string, limit?: number) => {
  const query = rawQuery.toLowerCase();

  const search = (threshold: FuzzySearchThreshold) =>
    fuzzySearch(query, index, {
      threshold,
      weights: { name: 1, altNames: 1, keywords: 0.1 },
    });

  const relevantSearchResults = search(FuzzySearchThreshold.Loose).filter(
    (result) => {
      const bestMatch = result.matches.toSorted((a, b) => b.score - a.score)[0];
      switch (bestMatch?.key) {
        case 'keywords':
          return bestMatch.indices.every((i) =>
            isIndexMatchingWordBoundary(bestMatch, i[0]),
          );
        case 'name':
        case 'altNames':
          return (
            bestMatch.indices[0] &&
            isIndexMatchingWordBoundary(bestMatch, bestMatch.indices[0][0])
          );
        default:
          return false;
      }
    },
  );

  return limit ? relevantSearchResults.slice(0, limit) : relevantSearchResults;
};

const zeroWidthJoiner = String.fromCodePoint(0x200d);
const presentationSelectors = String.fromCodePoint(0xfe0f);
const zwjRegExp = new RegExp(
  `${presentationSelectors}?${zeroWidthJoiner}`,
  'g',
);

/**
 * Apply a skin tone modifier to emoji
 *
 * Generally, to apply a skin tone modifier to an emoji:
 * 1. If the emoji is a an emoji character, we can simply append the modifier:
 *    👩 -> 👩 + MODIFIER -> 👩🏽
 *
 * 2. If the emoji is a zwj sequence, we need to insert the modifier before ZWJ:
 *    👩‍💻 is a combination of 👩 + ZWJ + 💻.
 *    👩🏽‍💻 is a combination of 👩 + MODIFIER + ZWJ + 💻.
 *
 * 3. Some characters and sequences in emojilib contain presentation selector that
 * is used to request an emoji presentation (versus text presentation) for an
 * emoji character. For theses sequences we need to remove it
 * (see 2.4.1 in https://unicode.org/reports/tr51/):
 *    🖐️ is a combination of 🖐 (unqualified) + PRESENTATION_SELECTOR
 *    🖐🏽 is a combination of 🖐 + MODIFIER
 *
 *    🕵️‍♀️ is a combination of 🕵️ + PRESENTATION_SELECTOR + ZWJ + ♀️
 *    🕵🏽‍♀️ is a combination of 🕵️ + MODIFIER + ZWJ + ♀️
 *
 * As recommended by 2.7.1, this function always returns fully-qualified sequences.
 *
 * @see
 * 1. ZWJ: https://en.wikipedia.org/wiki/Zero-width_joiner
 * 2. Presentation selector: https://unicode.org/reports/tr51/#def_emoji_presentation_selector
 */
export const applySkinToneToEmoji = (
  { char, fitzpatrickScale }: Pick<Emoji, 'char' | 'fitzpatrickScale'>,
  modifier: string | null,
) => {
  if (modifier == null || !fitzpatrickScale) return char;
  if (zwjRegExp.test(char)) {
    return char.replace(zwjRegExp, `${modifier}${zeroWidthJoiner}`);
  }
  if (char[char.length - 1] === presentationSelectors) {
    return char.slice(0, char.length - 1) + modifier;
  }
  return char + modifier;
};

const modifierAndSelectorRegExp = new RegExp(
  `[${skinToneModifiers.join('')}${presentationSelectors}${zeroWidthJoiner}]`,
  'g',
);

// Normalize an emoji by removing all modifiers to create a simplest
// representation (maybe unqualified).
const removeAllModifiers = (char: string) =>
  char.replace(modifierAndSelectorRegExp, '');
const emojiItemLookup = _.keyBy(emojiList, (emoji) =>
  removeAllModifiers(emoji.char),
);
const emojiItemNameLookup = _.keyBy(emojiList, (emoji) => emoji.name);

/**
 * Get the emoji item by a character/sequence.
 *
 * It worth noticing that for inputs that are skin tone modified, we don't
 * want to remove the skin tone as that will require an external lookup table
 * (ex https://github.com/iamcal/emoji-data) in order to get a fully-qualified
 * result.
 * Instead, we create a lookup table whose keys are "normalized" by removing
 * all modifiers, and we'll do the same thing to inputs so their skin tone
 * modifiers will be removed as well.
 */
export const getEmojiItemByChar = (char: string) =>
  emojiItemLookup[removeAllModifiers(char)];
export const getEmojiItemByName = (name: string) =>
  emojiItemNameLookup[removeAllModifiers(name)];

const DEFAULT_EMOJI_CHARS = ['😀', '👏', '🚀', '👍', '👎', '🤔'] as const;

export const DEFAULT_EMOJI_NAMES = _.compact(
  DEFAULT_EMOJI_CHARS.map((emoji) => getEmojiItemByChar(emoji)?.name),
);

export const isEmojiChar = (string: string) => {
  if (typeof string !== 'string') return false;
  return !!getEmojiItemByChar(string);
};
